import type { CptModel } from '@/models/cpt'

type RawLocationExtras = {
  term_locations?: unknown
  menu_order?: number
  featured_media?: number

  acf?: {
    color_theme?: string

    excerpt?: {
      icon?: string
      super_title?: string
      excerpt_text?: string
    }

    details?: {
      location_details?: {
        address_details?: {
          address?: string
          postal_code?: string
          city?: string
        }
        contact_details?: {
          email?: string
        }
      }
    }

    gallery?: {
      settings?: { title?: string }
      gallery_items?: {
        item?: Array<{
          image?: number
          caption?: string
        }>
      }
      anchor?: {
        show_element?: boolean
        menu_label?: string
        uuid?: string
      }
    }

    components?: unknown[] | false
  }
}

export type LocationModel = CptModel & {
  type: 'location'

  menuOrder?: number
  featuredMedia?: number
  termLocations: number[]

  colorTheme?: string

  excerpt?: {
    superTitle?: string
    text?: string
    icon?: string
  }

  address?: {
    address?: string
    postalCode?: string
    city?: string
  }

  contact?: {
    email?: string
  }

  gallery?: {
    title?: string
    items: Array<{ imageId: number; caption?: string }>
  }

  components?: unknown[] | false
}

const toNumberArray = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []

const location = (p: CptModel): LocationModel => {
  const raw = (p.raw ?? {}) as RawLocationExtras

  const addressDetails = raw.acf?.details?.location_details?.address_details
  const contactDetails = raw.acf?.details?.location_details?.contact_details

  const galleryItems = raw.acf?.gallery?.gallery_items?.item ?? []
  const mappedGalleryItems = Array.isArray(galleryItems)
    ? galleryItems
        .map((it) => ({
          imageId: typeof it.image === 'number' ? it.image : 0,
          caption: typeof it.caption === 'string' ? it.caption : undefined,
        }))
        .filter((it) => it.imageId > 0)
    : []

  return {
    ...p,
    type: 'location',

    menuOrder: raw.menu_order,
    featuredMedia: typeof raw.featured_media === 'number' ? raw.featured_media : p.media,
    termLocations: toNumberArray(raw.term_locations),

    colorTheme: raw.acf?.color_theme,

    excerpt: raw.acf?.excerpt
      ? {
          superTitle: raw.acf.excerpt.super_title,
          text: raw.acf.excerpt.excerpt_text,
          icon: raw.acf.excerpt.icon,
        }
      : undefined,

    address: addressDetails
      ? {
          address: addressDetails.address,
          postalCode: addressDetails.postal_code,
          city: addressDetails.city,
        }
      : undefined,

    contact: contactDetails
      ? {
          email: contactDetails.email,
        }
      : undefined,

    gallery: raw.acf?.gallery
      ? {
          title: raw.acf.gallery.settings?.title,
          items: mappedGalleryItems,
        }
      : undefined,

    components: raw.acf?.components ?? undefined,
  }
}

export default location
